import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, MouseEvent } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

// import { ReactComponent as CalendarIcon } from '@/assets/icons/calendar.svg';
import { ReactComponent as HyperlinkIcon } from '@/assets/icons/hyperlink.svg';
import { ReactComponent as IgnoreIcon } from '@/assets/icons/ignore.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td, Thumbnail } from '@/components/Table';
import Text from '@/components/Text';
import useNetwork from '@/hooks/dq/useNetwork';
import useAlerts from '@/hooks/useAlerts';
import useTableColumnViewOption from '@/hooks/useTableColumnViewOption';
import { EditorialFacebookPostResource } from '@/types/resources';
import { api } from '@/utils/api';
import { getNetworkLabel } from '@/utils/dq';
import {
  EditorialDistributionBenchmarkReach,
  EditorialDistributionStatus,
  EditorialFacebookPostType,
} from '@/utils/enums';
import { Color } from '@/utils/theme';

import { Checkbox, CheckboxHolder, CheckboxLabel } from './ArchiveTable';
import PostType from './PostType';
import { tableColumns } from './PostsTable';

const LinkTd = styled(Td)`
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
`;

const ChannelHolder = styled.div`
  display: flex;
  align-items: center;
`;

const ThumbnailTd = styled(Td)`
  ${Thumbnail} {
    max-width: 89px;
  }
`;

const HyperLink = styled('a').attrs({
  rel: 'noreferrer noopener',
  target: '_blank',
})`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  svg {
    margin-right: 6px;
    width: 14px;
    flex-shrink: 0;
  }
  > span {
    overflow-x: hidden;
    text-overflow: ellipsis;
  }
`;

const Time = styled(Text)`
  margin-top: 4px;
`;

const Image = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 6px;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const ChannelName = styled.span`
  white-space: nowrap;
`;

const Indicator = styled.div`
  display: flex;
  align-items: center;
`;

const Dot = styled.div<{ $color: Color }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 8px;
  background: ${({ $color, theme }) => theme.colors[$color]};
`;

const QueueBadge = styled(Text).attrs({
  $type: 'extraSmall',
  $color: 'gray',
  as: 'div',
})`
  display: inline-block;
  padding: 3px 4px;
  border-radius: 4px;
  margin-top: 8px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
`;

const CheckboxTd = styled(Td)`
  vertical-align: middle !important;
`;

const NetworkLabel = styled.span`
  text-transform: capitalize;
`;

const DepartmentsList = styled.ul`
  list-style-type: disc;
  list-style-position: inside;
`;

interface Props {
  schedule: EditorialFacebookPostResource;
  isChecked: boolean;
  onCheck: (id: number) => void;
}

const ArchivedPostTableRow: FC<Props> = ({ schedule, isChecked, onCheck }) => {
  const alerts = useAlerts();
  const { status } = useParams();
  // eslint-disable-next-line
  const [_, networkLabel] = useNetwork();

  const queryClient = useQueryClient();

  const tableViewOptionsKey = `${status!}_${networkLabel}_dq_posts_table`;

  const getColumnVisibility = useTableColumnViewOption(
    tableViewOptionsKey,
    tableColumns,
  );

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to delete this post?`,
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/editorial/facebook-posts/${schedule.id}`,
      );

      await queryClient.invalidateQueries(
        ['editorial_distribution_schedules'],
        {
          exact: false,
        },
      );

      alerts.success('Post was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  const onIgnore = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        `Are you sure you want to ignore this post?`,
      );

      if (!confirm) return;

      await api.patch(`/v1/editorial/facebook-posts/${schedule.id}/ignore`);

      await queryClient.invalidateQueries(
        ['editorial_distribution_schedules'],
        {
          exact: false,
        },
      );

      alerts.success('Post was successfully ignored');
    } catch (e) {
      alerts.error();
    }
  };

  const renderStatus = () => {
    if (schedule.status === EditorialDistributionStatus.SCHEDULED) {
      return (
        <Indicator>
          <Dot $color={'warning'} />
          Scheduled
        </Indicator>
      );
    }

    return (
      <Indicator>
        <Dot $color={'success'} />
        Posted
      </Indicator>
    );
  };

  const renderBenchmarkReach = () => {
    if (
      schedule.benchmarkReach ===
      EditorialDistributionBenchmarkReach.NOT_REACHED
    ) {
      return (
        <Indicator>
          <Dot $color={'error'} />
          Not Reached
        </Indicator>
      );
    }

    if (
      schedule.benchmarkReach === EditorialDistributionBenchmarkReach.REACHED
    ) {
      return (
        <Indicator>
          <Dot $color={'success'} />
          Reached
        </Indicator>
      );
    }

    return (
      <Indicator>
        <Dot $color={'gray'} />
        Pending
      </Indicator>
    );
  };

  const postHasLinkStats = [
    EditorialFacebookPostType.LINK,
    EditorialFacebookPostType.IMAGE,
  ].includes(schedule.type);

  return (
    <>
      <tr>
        <CheckboxTd>
          <CheckboxHolder>
            <Checkbox
              id={`s-${schedule.id}`}
              type="checkbox"
              checked={isChecked}
              onChange={() => onCheck(schedule.id)}
            />
            <CheckboxLabel htmlFor={`s-${schedule.id}`} />
          </CheckboxHolder>
        </CheckboxTd>
        {getColumnVisibility('Cover') && (
          <ThumbnailTd>
            <Thumbnail
              src={schedule.imageUrl}
              alt={'Cover'}
              type={
                schedule.type === EditorialFacebookPostType.REEL
                  ? 'reel'
                  : 'image'
              }
            />
          </ThumbnailTd>
        )}
        {getColumnVisibility('Message') && <Td>{schedule.message}</Td>}
        {getColumnVisibility('Network') && (
          <Td>
            <NetworkLabel>{getNetworkLabel(schedule.network)}</NetworkLabel>
          </Td>
        )}
        {getColumnVisibility('Page Name') && (
          <Td>
            <ChannelHolder>
              {!!schedule.channel.thumbnail && (
                <Image
                  src={schedule.channel.thumbnail}
                  alt={schedule.channel.name + ' thumbnail'}
                />
              )}
              <ChannelName>{schedule.channel.name}</ChannelName>
            </ChannelHolder>
          </Td>
        )}
        {getColumnVisibility('Content Type') && (
          <Td>
            <PostType type={schedule.type} />
          </Td>
        )}
        {getColumnVisibility('Status') && (
          <Td>
            {renderStatus()}
            {!!schedule.queueStatus && <QueueBadge>Queued</QueueBadge>}
          </Td>
        )}

        {getColumnVisibility('Benchmark Reach') && (
          <Td>{renderBenchmarkReach()}</Td>
        )}

        {getColumnVisibility('Deletion Date') && (
          <Td>
            {schedule.deletedAt ? (
              <>
                {DateTime.fromISO(schedule.deletedAt).toLocaleString()} <br />
                <Time $color="gray" $type="extraSmall">
                  {DateTime.fromISO(schedule.deletedAt).toFormat('HH:mm')}
                </Time>
              </>
            ) : (
              '-'
            )}
          </Td>
        )}
        {getColumnVisibility('Schedule Date') && (
          <Td>
            {DateTime.fromISO(schedule.createdAt).toLocaleString()} <br />
            <Time $color="gray" $type="extraSmall">
              {DateTime.fromISO(schedule.createdAt).toFormat('HH:mm')}
            </Time>
          </Td>
        )}
        {getColumnVisibility('Post Date') && (
          <Td>
            {schedule.publishedAt ? (
              <>
                {DateTime.fromISO(schedule.publishedAt).toLocaleString()} <br />
                <Time $color="gray" $type="extraSmall">
                  {DateTime.fromISO(schedule.publishedAt).toFormat('HH:mm')}
                </Time>
              </>
            ) : (
              '-'
            )}
          </Td>
        )}
        {getColumnVisibility('Site Visits') && (
          <Td>{!postHasLinkStats ? '-' : schedule.siteVisits ?? '-'}</Td>
        )}
        {getColumnVisibility('Site Visit Benchmark') && (
          <Td>{!postHasLinkStats ? '-' : schedule.siteVisitBenchmark}</Td>
        )}
        {getColumnVisibility('Reach Duration') && (
          <Td>{!postHasLinkStats ? '-' : schedule.reachDuration}</Td>
        )}
        {getColumnVisibility('Departments') && (
          <Td>
            {!!schedule.departments?.length ? (
              <DepartmentsList>
                {schedule.departments.map((dep) => (
                  <li key={dep.id}>{dep.name}</li>
                ))}
              </DepartmentsList>
            ) : (
              '-'
            )}
          </Td>
        )}
        {getColumnVisibility('Reactions') && (
          <Td>{schedule.reactions ?? '-'}</Td>
        )}
        {getColumnVisibility('Comments') && <Td>{schedule.comments ?? '-'}</Td>}
        {getColumnVisibility('Shares') && <Td>{schedule.shares ?? '-'}</Td>}

        {getColumnVisibility('Article Link') && (
          <LinkTd>
            {schedule.link ? (
              <HyperLink href={schedule.link}>
                <HyperlinkIcon />
                <span>{schedule.link}</span>
              </HyperLink>
            ) : (
              '-'
            )}
          </LinkTd>
        )}
        {getColumnVisibility('Post Link') && (
          <LinkTd>
            {schedule.facebookLink ? (
              <HyperLink href={schedule.facebookLink}>
                <HyperlinkIcon />
                <span>{schedule.facebookLink}</span>
              </HyperLink>
            ) : (
              '-'
            )}
          </LinkTd>
        )}
        {getColumnVisibility('Owner') && <Td>{schedule.owner}</Td>}
        {getColumnVisibility('Notes') && <Td>{schedule.notes ?? '-'}</Td>}
        {getColumnVisibility('Error Message') && (
          <Td>{schedule.error ?? '-'}</Td>
        )}
        <Td $actionsCol>
          <ActionsWrap>
            <ActionsDropdown>
              {/* <ActionsDropdownButton
                as="button"
                onClick={() => setShowModal(true)}
              >
                <span>
                  <CalendarIcon />
                </span>
                Repost
              </ActionsDropdownButton> */}
              <ActionsDropdownButton as="button" onClick={onIgnore}>
                <span>
                  <IgnoreIcon />
                </span>
                Ignore
              </ActionsDropdownButton>
              <ActionsDropdownButton as="button" onClick={onDelete}>
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
            </ActionsDropdown>
          </ActionsWrap>
        </Td>
      </tr>
    </>
  );
};

export default ArchivedPostTableRow;
